import { roundPriceCents } from '@bondsports/general';

import { CurrencySymbolEnum } from './constants';

/**
 * Remove commas in a price string and format it
 * with commas to separate every three digits in the integer part.
 *
 * @param price - The price string to be formatted
 * @returns The price string formatted with commas
 * separating every three digits in the integer part
 */
export const formatPriceWithCommas = (price: string) => {
	const priceWithoutCommas = price.replace(/,/g, '');
	return priceWithoutCommas.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Format a price with currency symbol and commas.
 *
 * @deprecated The Pricify function is deprecated, use the UI package formatPrice function instead.
 * @param price - The price to be formatted
 * @param currencySymbol - The currency symbol used to prefix the price
 * @param disableCurrencySymbol - If true, the output won't contain the currency symbol
 * @param fullPrice - If true, the output will always contain two decimal places
 * @returns The formatted price string
 */
export const Pricify = (
	price?: number,
	currencySymbol = CurrencySymbolEnum.USD,
	disableCurrencySymbol = false,
	fullPrice = false
) => {
	const currencySymbolDisplay = disableCurrencySymbol ? '' : currencySymbol;
	const errorString = `${currencySymbolDisplay}0${fullPrice ? '.00' : ''}`;

	if (price == null) return errorString;

	const pr = Number(price);
	const newPrice = roundPriceCents(Math.abs(pr));

	if (newPrice) {
		const decimalPrice = fullPrice ? newPrice.toFixed(2) : newPrice.toFixed(2).replace('.00', '');
		return pr > 0
			? `${currencySymbolDisplay}${formatPriceWithCommas(decimalPrice)}`
			: `-${currencySymbolDisplay}${formatPriceWithCommas(decimalPrice)}`;
	} else {
		return errorString;
	}
};

export interface ITransactionFeeAmounts {
	percentageValue?: number;
	fixValue?: number;
}

/**
 * Whether a configured transaction fee has a positive percentage or fixed amount.
 */
export const hasPositiveTransactionFee = (fee?: ITransactionFeeAmounts | null): boolean =>
	(fee?.fixValue ?? 0) > 0 || (fee?.percentageValue ?? 0) > 0;

/**
 * Format a transaction fee with a percentage and/or a fixed value.
 *
 * @param percentVal - The percentage value of the transaction fee
 * @param fixedVal - The fixed value of the transaction fee
 * @returns The formatted transaction fee
 */
export const formatTransactionFee = (percentVal?: string, fixedVal?: string): string => {
	const percent = Number(percentVal) > 0 ? `${roundPriceCents(Number(percentVal) * 100)}%` : '';
	const addition = Number(percentVal) > 0 && Number(fixedVal) > 0 ? ` + ` : '';
	const fixed = Number(fixedVal) > 0 ? `${Pricify(Number(fixedVal))} fixed` : ``;
	return `${percent}${addition}${fixed}`;
};
